'use strict';
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { normalizeAvatar } from './types';
import { chunkSubtitleText } from './util';
import RealtimeSession from './RealtimeSession';
import AvatarShell from './ui/AvatarShell';
import applyGuidedAnswer from '../siteSearch/components/applyGuidedAnswer';
import TalkingHeadRenderer from './renderers/TalkingHeadRenderer';
import HeygenRenderer from './renderers/HeygenRenderer';
import SimpleAvatarRenderer from './renderers/SimpleAvatarRenderer';
import SxQueryUtils from '../sxQuery/SxQueryUtils';
/**
 * Set on the root element while an avatar is mounted, so the avatar stylesheet can restyle the
 * comparison and the mini PDP as compact modals.
 *
 * On the root rather than the avatar's own element because neither surface is inside it: the
 * comparison is a custom element appended to `document.body`, and the mini PDP's backdrop covers the
 * page.
 */
const OVERLAY_SCOPE_CLASS = 'ss360-avatar-overlays';
/** Remembers whether the customer chose to speak or type, for this tab only. */
const INPUT_MODE_STORAGE_KEY = 'zoovu-avatar-input-mode';
/** Min gap between `search_products`-triggered reloads (avatar may emit bursts). */
const SEARCH_PRODUCTS_RELOAD_DEDUPE_WINDOW_MS = 250;
/**
 * Render sources that are NOT a user changing their selection — these must NOT
 * notify the backend (the initial reload would otherwise interrupt the avatar's
 * opening line).
 */
const NON_USER_SEARCH_SOURCES = ['reload', 'static', 'pageload', 'popstate'];
/** Normalize a comparison-identifiers payload (strings or `{identifier|id}` objects). */
function normalizeComparisonIdentifiers(rawIdentifiers) {
    if (!Array.isArray(rawIdentifiers)) {
        return null;
    }
    return rawIdentifiers
        .map((item) => {
        var _a, _b;
        if (typeof item === 'string')
            return item;
        if (item && typeof item === 'object') {
            const obj = item;
            return (_b = (_a = obj.identifier) !== null && _a !== void 0 ? _a : obj.id) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    })
        .filter(Boolean);
}
/**
 * Orchestrates the avatar experience inside the search plugin.
 *
 * The avatar is a **chromeless, transparent, click-through overlay** rendered at
 * the configured size/position over the search results — no panel, header,
 * controls, chat transcript, or start button. The realtime session **starts
 * automatically** when the plugin renders (hands-free, server-side VAD); the
 * only on-screen elements are the avatar itself and live subtitles.
 *
 * It drives the plugin's own result methods directly (reload / mini-PDP /
 * comparison) and forwards user search changes back to the backend.
 *
 * Ported from the Quasar `AssistantRunnerPage.vue`, minus Vue reactivity + chrome.
 */
export default class AvatarWidget {
    constructor(context, core, config, sessionId) {
        this.avatar = null;
        this.shell = null;
        this.renderer = null;
        this.session = null;
        this.sessionId = null;
        this.handlersBound = false;
        this.started = false;
        this.connected = false;
        // First-interaction audio unblock (autoplay policies) — see installAudioUnblockOnce().
        this.audioUnblockHandler = null;
        // Search integration state
        this.lastSearchProductsReloadAt = 0;
        // Streaming text/transcript accumulation
        this.currentTextResponse = '';
        this.currentAudioTranscript = '';
        this.currentSubtitleResponseId = null;
        /**
         * In the chat layout the avatar's turn is a message in the conversation rather than a caption.
         * Held across the deltas of one turn so the bubble streams in instead of stacking one per delta;
         * reset where each handler detects a new turn, NOT on `done` - a turn can finish with both
         * `response.text.done` and `response.audio_transcript.done`, and both must land on one bubble.
         */
        this.chatTurnId = null;
        /**
         * Which stream owns the avatar's current bubble.
         *
         * Two handlers can write the same bubble, and they disagree about what a "turn" is. Measured from a
         * real conversation:
         *
         * - A response that contains a tool call never produces `response.text.done` - the service skips
         *   `ProviderResponseCompleted` for intermediate responses - so `currentTextResponse` is never
         *   reset, and the *next* response's deltas append to it.
         * - That next response's `response.audio_transcript.done` carries only its OWN transcript, which is
         *   therefore shorter than the accumulated text.
         * - Rendering it hands the shell a shorter target for the same bubble, and a shorter target rewinds
         *   the reveal: the text visibly restarts and retypes itself.
         *
         * Hence "sometimes" - only a turn that speaks either side of a tool call can do it. The fix is that
         * one stream owns a turn: whichever speaks first, with the other ignored until the turn ends. The
         * deltas and the final transcript carry the same words, so it does not matter which wins, only that
         * one of them does. The intro still renders through the transcript handler, because it has no text
         * deltas to claim it.
         */
        this.subtitleSource = null;
        /** Speaking or typing, in the chat layout. Remembered per tab. */
        this.inputMode = 'voice';
        this.pageHideHandler = null;
        // Runtime-limit timer (enforcement only; no visible countdown)
        this.timerHandle = null;
        this.remainingSeconds = 0;
        this.context = context;
        this.core = core;
        this.config = config;
        // Share the search plugin's session id (from insights) when provided;
        // otherwise fall back to a fresh random id.
        this.sessionId = sessionId || SxQueryUtils.uuid();
    }
    /** Entry point: build the overlay, fetch config, load the visual, auto-start. */
    render() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            document.documentElement.classList.add(OVERLAY_SCOPE_CLASS);
            // Mount the (transparent) overlay first so a load state appears at once and
            // fetch failures surface on-screen rather than silently.
            this.buildShell();
            // Forward user-initiated search changes to the backend (bridge replacement).
            this.installSearchResultForwarder();
            yield this.loadAvatar();
            if (!this.avatar) {
                this.reportAvatarError(this.config.labels.loadError);
                return;
            }
            this.buildRenderer();
            // Load the avatar visual immediately (independent of the audio session).
            if (this.renderer) {
                void this.renderer.mount();
            }
            else {
                (_a = this.shell) === null || _a === void 0 ? void 0 : _a.showStageLoading(false);
            }
            // Start the conversation immediately — no user gesture / start button.
            void this.start();
        });
    }
    // ── Avatar fetch ──────────────────────────────────────────────────
    loadAvatar() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const url = `${this.config.endpoint}/projects/${encodeURIComponent(this.config.projectId)}/avatars/${encodeURIComponent(this.config.id)}`;
                // Keyless integration — no auth headers.
                const res = yield fetch(url, { method: 'GET' });
                if (!res.ok) {
                    throw new Error(`Failed to load avatar: ${res.status}`);
                }
                const raw = yield res.json();
                this.avatar = normalizeAvatar(raw);
            }
            catch (err) {
                console.error('[avatar] Failed to load avatar:', err);
                this.avatar = null;
            }
        });
    }
    // ── Overlay + renderer construction ───────────────────────────────
    buildShell() {
        this.shell = new AvatarShell(this.config, {
            onSendText: (text) => this.sendText(text),
            onInputModeChange: (mode) => this.applyInputMode(mode)
        });
        this.shell.mount();
        this.installSessionTeardown();
        this.shell.showStageLoading(true);
    }
    buildRenderer() {
        if (!this.shell || !this.avatar)
            return;
        const ctx = {
            container: this.shell.getStage(),
            // The chat layout's round bust needs a tighter frame than the stage viewport.
            framing: this.config.layout === 'chat' ? 'bust' : 'full',
            assetBase: this.config.assetBase,
            avatarBase: this.config.avatarBase,
            getAudioContext: () => (this.session ? this.session.getAudioContext() : null),
            getPlaybackGainNode: () => (this.session ? this.session.getPlaybackGainNode() : null),
            onLoaded: () => {
                var _a, _b, _c, _d;
                (_a = this.shell) === null || _a === void 0 ? void 0 : _a.showStageLoading(false);
                (_b = this.shell) === null || _b === void 0 ? void 0 : _b.setStageVisible(true);
                // Apply the avatar's start mood.
                this.applyMood(((_c = this.avatar) === null || _c === void 0 ? void 0 : _c.mood) || 'neutral');
                this.fireCallback('avatarRendered', (_d = this.shell) === null || _d === void 0 ? void 0 : _d.getStage());
            },
            onError: (e) => {
                this.reportAvatarError(e.message || this.config.labels.loadError);
            },
            onStateChange: (state) => {
                var _a;
                this.fireCallback('avatarStateChange', state, (_a = this.shell) === null || _a === void 0 ? void 0 : _a.getStage());
            }
        };
        if (this.avatar.avatarMode === 'HEYGEN') {
            // HeyGen (LiveKit streaming video). Connects once session.created
            // provides the LiveKit URL/token; loading stays until the stream is ready.
            this.renderer = new HeygenRenderer(ctx);
        }
        else if (this.avatar.avatarMode === 'SIMPLE') {
            // Still image animated by the conversation audio (no 3D, no video).
            // Decorations always use the plugin accent colour ($avatar-accent).
            this.renderer = new SimpleAvatarRenderer(ctx, this.avatar.simpleAvatarConfig || null);
        }
        else {
            this.renderer = new TalkingHeadRenderer(ctx, this.avatar.avatarId || null);
        }
    }
    // ── Session lifecycle ─────────────────────────────────────────────
    /** Begin the conversation automatically (no user gesture). */
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.started || !this.avatar)
                return;
            this.started = true;
            (_a = this.shell) === null || _a === void 0 ? void 0 : _a.clearError();
            (_b = this.shell) === null || _b === void 0 ? void 0 : _b.setPhase('connecting');
            // Browsers may block audio output until the first user interaction; resume
            // on the first click/keypress anywhere so we can still auto-start.
            this.installAudioUnblockOnce();
            try {
                this.ensureSession();
                yield this.session.resumeAudio();
                yield this.session.connect();
            }
            catch (err) {
                console.error('[avatar] Failed to start session:', err);
                this.reportAvatarError(this.config.labels.connectError);
                this.started = false;
            }
        });
    }
    /**
     * End the realtime session when the page goes away.
     *
     * Without this the socket is only reaped by the backend's own timeout, so every navigation leaves a
     * session running: 74 of them accumulated in one local run. It costs realtime-API minutes, and it
     * makes "why can I hear two avatars" much easier to hit, because a session the customer thinks they
     * left is still live.
     *
     * `pagehide` rather than `beforeunload`: it is the one that fires on mobile Safari and when the page
     * goes into the back/forward cache, which `beforeunload` does not.
     */
    installSessionTeardown() {
        if (this.pageHideHandler)
            return;
        this.pageHideHandler = () => {
            var _a;
            try {
                (_a = this.session) === null || _a === void 0 ? void 0 : _a.disconnect();
            }
            catch (e) { /* the page is going away regardless */ }
        };
        window.addEventListener('pagehide', this.pageHideHandler);
    }
    /**
     * Register a one-shot listener that resumes audio playback on the first user
     * interaction with the page — the invisible replacement for the old "Start"
     * button's gesture, needed for browser autoplay policies.
     */
    installAudioUnblockOnce() {
        if (this.audioUnblockHandler)
            return;
        const handler = () => {
            var _a, _b, _c;
            void ((_a = this.session) === null || _a === void 0 ? void 0 : _a.resumeAudio());
            (_c = (_b = this.renderer) === null || _b === void 0 ? void 0 : _b.resumeMedia) === null || _c === void 0 ? void 0 : _c.call(_b);
            this.removeAudioUnblock();
        };
        this.audioUnblockHandler = handler;
        document.addEventListener('pointerdown', handler);
        document.addEventListener('keydown', handler);
    }
    removeAudioUnblock() {
        if (!this.audioUnblockHandler)
            return;
        document.removeEventListener('pointerdown', this.audioUnblockHandler);
        document.removeEventListener('keydown', this.audioUnblockHandler);
        this.audioUnblockHandler = null;
    }
    ensureSession() {
        if (!this.sessionId)
            this.sessionId = SxQueryUtils.uuid();
        if (!this.session) {
            this.session = new RealtimeSession(this.config.id, this.config.projectId, this.sessionId, this.config.endpoint, this.config.assetBase);
        }
        this.session.sessionId = this.sessionId;
        if (this.avatar) {
            this.session.setCommunicationType(this.avatar.communicationType);
        }
        this.bindSessionHandlers();
    }
    bindSessionHandlers() {
        var _a;
        if (this.handlersBound || !this.session)
            return;
        const s = this.session;
        // The shell paces the avatar's written transcript against this, so the words on screen are the
        // words being spoken rather than a paragraph that has run ahead of the voice.
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.setSpeechClock(() => s.getSpeechProgress());
        s.on('session.created', (m) => this.handleSessionCreated(m));
        s.on('session.updated', () => this.handleSessionUpdated());
        s.on('response.text.delta', (m) => this.handleTextDelta(m));
        s.on('response.text.done', (m) => this.handleTextDone(m));
        s.on('response.audio_transcript.delta', (m) => this.handleAudioTranscriptDelta(m));
        s.on('response.audio_transcript.done', (m) => this.handleAudioTranscriptDone(m));
        // A response boundary ends the turn, whichever stream claimed it. Releasing only on the owner's
        // own `done` deadlocks: the service skips `response.text.done` for the intermediate response
        // carrying a tool call, so the first tool call would hold the lock for the whole session.
        // `response.created` too, because the missing event IS the terminal one.
        ['response.created', 'response.done', 'response.cancelled']
            .forEach((evt) => s.on(evt, () => this.endSubtitleTurn(true)));
        s.on('conversation.item.input_audio_transcription.completed', (m) => this.handleInputTranscription(m));
        s.on('mood.detected', (m) => this.handleMoodDetected(m));
        // Server-side mic VAD → renderer listening state (Simple renderer only).
        s.on('speech_started', () => {
            var _a, _b, _c, _d;
            (_b = (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.setListening) === null || _b === void 0 ? void 0 : _b.call(_a, true);
            (_c = this.shell) === null || _c === void 0 ? void 0 : _c.setSpeaking(true);
            // The customer's bubble belongs on screen from the first word, not from the end of the
            // sentence: it is where their words are going to land, and seeing it appear is the
            // confirmation that they are being heard.
            if (this.config.layout === 'chat') {
                (_d = this.shell) === null || _d === void 0 ? void 0 : _d.beginUserTurn();
            }
        });
        s.on('speech_stopped', () => {
            var _a, _b, _c, _d;
            (_b = (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.setListening) === null || _b === void 0 ? void 0 : _b.call(_a, false);
            (_c = this.shell) === null || _c === void 0 ? void 0 : _c.setSpeaking(false);
            // Now the reply is what we are waiting for.
            if (this.config.layout === 'chat') {
                (_d = this.shell) === null || _d === void 0 ? void 0 : _d.beginAvatarWait();
            }
        });
        s.on('assistant.interrupted', (m) => this.handleAssistantInterrupted(m));
        s.on('error', (m) => this.handleError(m));
        s.on('close', () => this.handleClose());
        // Tool events drive the search plugin's own result methods.
        ['tool_result', 'search_products', 'get_product_details', 'compare_products', 'product_details', 'comparison']
            .forEach((evt) => s.on(evt, (m) => this.handleToolEvent(evt, m)));
        this.handlersBound = true;
    }
    handleSessionCreated(message) {
        var _a, _b, _c, _d, _e;
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.setPhase('connected');
        this.connected = true;
        (_b = this.shell) === null || _b === void 0 ? void 0 : _b.clearError();
        if (message.sessionId) {
            this.sessionId = message.sessionId;
        }
        this.startTimerIfNeeded();
        // HeyGen renderer needs the LiveKit creds carried in session.created;
        // TalkingHead ignores this hook.
        (_d = (_c = this.renderer) === null || _c === void 0 ? void 0 : _c.onSessionCreated) === null || _d === void 0 ? void 0 : _d.call(_c, message);
        if (this.session) {
            this.session.resumeAudio().then(() => {
                // The playback AudioContext is created lazily; wire lip-sync now
                // that it exists (the GLB may have mounted before the session).
                if (this.renderer) {
                    void this.renderer.attachAudio();
                }
            });
            const a = this.avatar;
            // For voice/avatar the backend already sent the full session.update to
            // OpenAI during init — do NOT send a second one. Only text mode does.
            if (a && !this.isVoiceOrAvatar()) {
                this.session.updateSession({
                    instructions: a.instructions,
                    voice: a.voice,
                    communicationType: a.communicationType,
                    language: a.language,
                    mood: a.mood,
                    introSentences: a.introSentences
                });
            }
            else if (a) {
                this.session.setCommunicationType(a.communicationType);
            }
            // Hands-free: the mic is always on in voice/avatar mode (server VAD) - unless the customer
            // has chosen to type, which the chat layout lets them do and remembers.
            if (this.config.layout === 'chat') {
                const mode = this.resolveInitialInputMode();
                (_e = this.shell) === null || _e === void 0 ? void 0 : _e.setInputMode(mode);
                this.applyInputMode(mode);
            }
            else if (this.isVoiceOrAvatar()) {
                void this.startRecording();
            }
        }
    }
    handleSessionUpdated() {
        // Kick off the avatar's intro for this fresh session (text mode only;
        // for voice/avatar the backend triggers it).
        if (this.session) {
            this.session.sendMessage({ type: 'response.create' });
        }
    }
    // ── Streaming responses → subtitles ───────────────────────────────
    // Where the avatar's own output goes depends on the layout: captions under the avatar in the
    // `stage` layout, a message in the conversation in the `chat` layout. The user's typed messages
    // always go to the conversation.
    handleTextDelta(message) {
        var _a;
        const delta = typeof message.delta === 'string' ? message.delta : (((_a = message.delta) === null || _a === void 0 ? void 0 : _a.text) || '');
        if (!delta)
            return;
        if (this.subtitleSource === 'audio') {
            return; // the spoken transcript owns this turn
        }
        if (!this.currentTextResponse) {
            this.chatTurnId = null; // first delta of a new turn
            this.subtitleSource = 'text';
        }
        this.currentTextResponse += delta;
        const clean = this.currentTextResponse.replace(/search\("[^"]*"\)/g, '').trim();
        this.renderSubtitle(clean);
    }
    handleTextDone(message) {
        if (this.subtitleSource === 'audio') {
            this.currentTextResponse = '';
            return;
        }
        const raw = message.text || this.currentTextResponse;
        const finalContent = raw.replace(/search\("[^"]*"\)/g, '').trim();
        this.renderSubtitle(finalContent);
        this.currentTextResponse = '';
        this.subtitleSource = null;
    }
    handleAudioTranscriptDelta(message) {
        var _a, _b;
        const resId = message.response_id || message.responseId || ((_a = message.response) === null || _a === void 0 ? void 0 : _a.id) || 'current-response';
        const delta = typeof message.delta === 'string'
            ? message.delta
            : (typeof message.transcript === 'string' ? message.transcript : (((_b = message.delta) === null || _b === void 0 ? void 0 : _b.transcript) || ''));
        if (!delta)
            return;
        if (this.subtitleSource === 'text') {
            return; // the text stream got here first and owns this turn
        }
        if (this.currentSubtitleResponseId !== resId) {
            this.currentSubtitleResponseId = resId;
            this.currentAudioTranscript = delta;
            this.chatTurnId = null; // a different response id means a new turn
            this.subtitleSource = 'audio';
        }
        else {
            this.currentAudioTranscript += delta;
        }
        this.renderSubtitle(this.currentAudioTranscript);
    }
    handleAudioTranscriptDone(message) {
        if (this.subtitleSource === 'text') {
            return;
        }
        const finalContent = message.transcript || this.currentAudioTranscript;
        this.renderSubtitle(finalContent);
        this.currentAudioTranscript = '';
        this.currentSubtitleResponseId = null;
        this.subtitleSource = null;
    }
    /** Show the latest response - as a conversation message in `chat`, as captions in `stage`. */
    renderSubtitle(text) {
        var _a, _b;
        if (this.config.layout === 'chat') {
            if (!text)
                return;
            if (!this.chatTurnId) {
                this.chatTurnId = `avatar-${Date.now()}`;
            }
            (_a = this.shell) === null || _a === void 0 ? void 0 : _a.upsertMessage({
                id: this.chatTurnId,
                sender: 'avatar',
                content: text,
                timestamp: Date.now()
            }, 'speech'); // deltas arrive in bursts; the reveal keeps time with the voice
            return;
        }
        const compact = (typeof window !== 'undefined' && window.innerWidth < 600);
        const maxChars = compact ? 110 : 160;
        const windowSize = compact ? 2 : 3;
        const chunks = chunkSubtitleText(text, maxChars);
        (_b = this.shell) === null || _b === void 0 ? void 0 : _b.setSubtitle(chunks.slice(-windowSize).join('\n\n'));
    }
    /**
     * What the customer just said, shown in the conversation exactly as a typed message would be.
     *
     * Arrives complete rather than as deltas (the provider transcribes the whole utterance), so it is
     * streamed in for the same reason the avatar's text is: a whole sentence appearing at once reads as
     * a different kind of event from the one the customer is having.
     */
    handleInputTranscription(message) {
        var _a;
        if (this.config.layout !== 'chat')
            return;
        const transcript = (message.transcript || '').toString().trim();
        if (!transcript)
            return;
        // Always its own id. The shell hands the waiting slot over to it if one is standing, so the
        // transcript still lands in place - but the slot's id stays the slot's, and cannot be mistaken
        // for a message on the next turn.
        // Set at once, not typed out. The customer already knows what they said - watching it appear a
        // character at a time is a re-enactment, not information, and it delays the reply they are
        // actually waiting for. The bubble fades the words in instead (`__msg--revealed`).
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.upsertMessage({
            id: `user-said-${Date.now()}`,
            sender: 'user',
            content: transcript,
            timestamp: Date.now()
        });
    }
    /**
     * A turn ended without a reply.
     *
     * Only one reason needs to reach the customer: the connection to the provider was lost. Everything
     * else here - a barge-in, an answer click, refreshed results - is a normal part of the conversation
     * and should say nothing. A lost connection otherwise shows as nothing at all: no words, no error,
     * and dots that spin until they time out, which is indistinguishable from the avatar ignoring you.
     */
    handleAssistantInterrupted(message) {
        var _a;
        // Whatever the reason, the turn is over: release the bubble so the next one is not locked to a
        // stream that will never send its `done`. Without this, one barge-in silenced the losing stream
        // for the rest of the session.
        this.endSubtitleTurn();
        if (message.reason !== 'provider_disconnected')
            return;
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.cancelPendingTurn();
        this.reportAvatarError(this.config.labels.connectError);
    }
    /**
     * Releases the avatar's current bubble and the stream that owned it.
     *
     * @param keepBubble keep `chatTurnId`, so a late `done` resolves its own bubble instead of opening
     *                   a second one. The next turn still gets a fresh id - the delta handlers mint one
     *                   off the accumulators this clears, not off `chatTurnId`.
     */
    endSubtitleTurn(keepBubble = false) {
        this.subtitleSource = null;
        this.currentTextResponse = '';
        this.currentAudioTranscript = '';
        this.currentSubtitleResponseId = null;
        if (!keepBubble) {
            this.chatTurnId = null;
        }
    }
    handleMoodDetected(message) {
        if (message.mood) {
            this.applyMood(message.mood);
        }
    }
    handleError(event) {
        var _a;
        console.error('[avatar] session error:', event);
        if (event && event.type === 'error' && event.error) {
            const err = event.error;
            // Suppress the transient ALLOY voice-casing error (fixed via update).
            if (err.message && err.message.includes("'ALLOY'"))
                return;
            const fatalCodes = ['invalid_token', 'connection_error', 'session_expired'];
            if (err.code !== undefined && fatalCodes.indexOf(err.code) !== -1) {
                this.reportAvatarError(`Error: ${(_a = err.message) !== null && _a !== void 0 ? _a : ''}`);
            }
        }
        else if (event && event.message) {
            this.reportAvatarError(event.message);
        }
    }
    handleClose() {
        this.connected = false;
        // The session is gone — stop enforcing the runtime limit (it would
        // otherwise keep ticking and call end()/disconnect() on a dead socket)
        // and drop the one-shot audio-unblock listeners.
        this.stopTimer();
        this.removeAudioUnblock();
        if (this.shell && this.started) {
            this.shell.setPhase('ended');
        }
    }
    // ── Search-plugin integration ─────────────────────────────────────
    //
    // The avatar lives inside the plugin, so it drives the plugin's own result
    // methods directly (no cross-window `window.ZOOVU_SEARCH` calls), and forwards
    // user-initiated search changes back to the backend over the WS (the in-plugin
    // replacement for the old `window.__ZOE_ZOOVU_SOCKET_BRIDGE__`).
    handleToolEvent(evt, message) {
        if (evt === 'search_products') {
            this.handleSearchProducts(message);
        }
        else {
            this.handleToolResult(message);
        }
    }
    /** `search_products` → reload the plugin's result set for the session (deduped). */
    handleSearchProducts(message) {
        var _a, _b, _c, _d, _e, _f, _g;
        const toolSessionId = (_g = (_f = (_d = (_c = (_b = (_a = message.result) === null || _a === void 0 ? void 0 : _a.sessionId) !== null && _b !== void 0 ? _b : message.sessionId) !== null && _c !== void 0 ? _c : message.session_id) !== null && _d !== void 0 ? _d : (_e = message.result) === null || _e === void 0 ? void 0 : _e.session_id) !== null && _f !== void 0 ? _f : this.sessionId) !== null && _g !== void 0 ? _g : null;
        if (!toolSessionId) {
            console.error('[avatar] search_products event missing sessionId', message);
            return;
        }
        // Dedupe rapid repeats.
        const now = Date.now();
        if (now - this.lastSearchProductsReloadAt < SEARCH_PRODUCTS_RELOAD_DEDUPE_WINDOW_MS) {
            return;
        }
        this.lastSearchProductsReloadAt = now;
        this.safeCall('reloadResults', () => this.core.reloadResults(toolSessionId));
    }
    /** Dispatch a tool result (or `tool_result` wrapper) to the right core method. */
    handleToolResult(message) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
        const toolName = (_a = message.tool) !== null && _a !== void 0 ? _a : message.type;
        if (toolName === 'search_products') {
            this.handleSearchProducts(message);
            return;
        }
        if (toolName === 'get_product_details' || toolName === 'product_details') {
            const identifier = (_g = (_e = (_c = (_b = message.identifier) !== null && _b !== void 0 ? _b : message.id) !== null && _c !== void 0 ? _c : (_d = message.result) === null || _d === void 0 ? void 0 : _d.identifier) !== null && _e !== void 0 ? _e : (_f = message.result) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : (_h = message.product) === null || _h === void 0 ? void 0 : _h.identifier;
            if (!identifier) {
                console.error('[avatar] get_product_details event missing identifier', message);
                return;
            }
            this.safeCall('openMiniPDP', () => this.core.openMiniPDP(identifier));
            return;
        }
        if (toolName === 'compare_products' || toolName === 'comparison') {
            const identifiers = normalizeComparisonIdentifiers((_r = (_p = (_m = (_k = (_j = message.identifiers) !== null && _j !== void 0 ? _j : message.products) !== null && _k !== void 0 ? _k : (_l = message.result) === null || _l === void 0 ? void 0 : _l.identifiers) !== null && _m !== void 0 ? _m : (_o = message.result) === null || _o === void 0 ? void 0 : _o.products) !== null && _p !== void 0 ? _p : (_q = message.comparison) === null || _q === void 0 ? void 0 : _q.identifiers) !== null && _r !== void 0 ? _r : (_t = (_s = message.result) === null || _s === void 0 ? void 0 : _s.comparison) === null || _t === void 0 ? void 0 : _t.identifiers);
            if (!identifiers || identifiers.length === 0) {
                console.error('[avatar] compare_products event missing identifiers array', message);
                return;
            }
            this.safeCall('startComparison', () => this.core.startComparison(identifiers));
        }
    }
    /**
     * Surface a renderer/session failure where the layout can actually show it.
     *
     * In the `stage` layout that is the error overlay on the avatar viewport. In `chat` the viewport is
     * a 108px circle - a sentence is unreadable there - so the message goes into the conversation and
     * the bust keeps its ring, which reads as "the avatar is not here" rather than as a broken panel.
     */
    reportAvatarError(message) {
        var _a, _b, _c, _d;
        if (this.config.layout === 'chat') {
            (_a = this.shell) === null || _a === void 0 ? void 0 : _a.showStageLoading(false);
            (_b = this.shell) === null || _b === void 0 ? void 0 : _b.setPhase('error');
            (_c = this.shell) === null || _c === void 0 ? void 0 : _c.upsertMessage({
                id: 'avatar-error',
                sender: 'avatar',
                content: message,
                timestamp: Date.now()
            });
            return;
        }
        (_d = this.shell) === null || _d === void 0 ? void 0 : _d.showError(message);
    }
    /** Run a plugin-core call, logging (not throwing) on failure. */
    safeCall(label, fn) {
        try {
            fn();
        }
        catch (err) {
            console.error(`[avatar] core.${label} failed`, err);
        }
    }
    /**
     * Install a `callbacks.postSearch` wrapper so that when the user changes the
     * search (filters / answers / query) the plugin notifies the backend over the
     * WS — replacing the old socket bridge. Non-user render sources are skipped so
     * the avatar's opening line is never interrupted. Chains any pre-existing
     * `postSearch` the host may have configured.
     */
    installSearchResultForwarder() {
        const callbacks = this.context && this.context.pluginConfiguration
            ? this.context.pluginConfiguration.callbacks
            : undefined;
        if (!callbacks) {
            console.warn('[avatar] plugin callbacks unavailable; search changes will not be forwarded');
            return;
        }
        const prev = callbacks.postSearch;
        const self = this;
        // `data` is contextually typed from PostSearchCallback; the plugin also
        // passes a `submitSource` extra arg at runtime, annotated explicitly here.
        callbacks.postSearch = function (data, submitSource) {
            if (typeof prev === 'function') {
                try {
                    prev.call(this, data, submitSource);
                }
                catch (e) {
                    console.warn('[avatar] prior postSearch failed', e);
                }
            }
            self.forwardSearchResultsChanged(data, submitSource);
        };
    }
    /**
     * Render the search's still-open guided questions as chips in the conversation.
     *
     * Called by `core.renderResults` in place of rendering the answer band, so it sees the same
     * `SearchResponse` the band would have, on every render including the very first — a `postSearch`
     * subscription would miss that one, because the first search completes before this chunk loads.
     *
     * Semknox drops a question from the result as soon as it is answered — including a null/skip
     * answer — so whatever arrives is exactly what is still open. No client-side bookkeeping, and no
     * question can be asked twice.
     *
     * Only the first open question is offered: the panel is a conversation, and four questions at once
     * would be the answer band again, in a smaller box.
     */
    renderGuidedQuestions(questions, context, data) {
        if (!this.shell || !questions || questions.length === 0)
            return;
        const question = questions.find((q) => !!q && !!q.answers && q.answers.length > 0);
        if (!question)
            return;
        // Correlates the insights beacon with this render, matching the answer band's own viewId.
        const viewId = Math.round(Math.random() * 1000000);
        this.shell.renderQuestion({
            id: String(question.id),
            text: question.question || '',
            answers: question.answers.map((a) => ({
                id: a.id === undefined ? null : a.id,
                label: a.short_name || a.answer_text || ''
            })),
            onAnswer: (answer, index) => {
                var _a;
                // Clicking an answer and saying it out loud are the same act, so they read the same in
                // the transcript: the customer's turn, in their own words, before the avatar replies.
                (_a = this.shell) === null || _a === void 0 ? void 0 : _a.upsertMessage({
                    id: `answered-${question.id}`,
                    sender: 'user',
                    content: answer.label,
                    timestamp: Date.now()
                });
                this.safeCall('applyGuidedAnswer', () => {
                    applyGuidedAnswer(context, data, question, { id: answer.id, short_name: answer.label }, viewId, index);
                });
            }
        });
    }
    /**
     * Whether the answer band above the results should stand down because this avatar is asking the
     * questions instead. Requires the panel to actually exist — if the avatar chunk fails to load or
     * the renderer dies, the band stays and the customer can still answer.
     */
    ownsGuidedQuestions() {
        return this.config.layout === 'chat' && !!this.shell;
    }
    forwardSearchResultsChanged(data, submitSource) {
        if (submitSource && NON_USER_SEARCH_SOURCES.indexOf(submitSource) !== -1) {
            return;
        }
        if (!this.connected || !this.session || !this.sessionId) {
            return;
        }
        this.session.sendMessage({
            type: 'search_results.changed',
            sessionId: this.sessionId,
            searchResult: data
        });
    }
    // ── Microphone (hands-free) ───────────────────────────────────────
    startRecording() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.session || this.session.isStreaming)
                return;
            try {
                // The level callback drives the panel's meter; harmless in the stage layout, which has no
                // meter to drive.
                yield this.session.startRecording((level) => { var _a; return (_a = this.shell) === null || _a === void 0 ? void 0 : _a.setInputLevel(level); });
            }
            catch (err) {
                console.error('[avatar] Microphone error:', err);
                // The mic is not available (denied, in use, no device). Fall back to typing rather than
                // leaving a switch that claims to be listening when nothing is.
                if (this.config.layout === 'chat') {
                    this.applyInputMode('text');
                    (_a = this.shell) === null || _a === void 0 ? void 0 : _a.setInputMode('text');
                }
            }
        });
    }
    /**
     * Switch between speaking and typing.
     *
     * Text mode **releases** the microphone rather than muting it: `stopRecording()` stops the media
     * tracks, so the browser's own recording indicator goes out. That indicator is the only signal about
     * the microphone a customer can actually trust, and a muted-but-open mic makes it lie.
     *
     * Remembered for the tab, so switching pages does not silently reopen the microphone on someone who
     * chose to type.
     */
    applyInputMode(mode) {
        var _a, _b;
        this.inputMode = mode;
        try {
            window.sessionStorage.setItem(INPUT_MODE_STORAGE_KEY, mode);
        }
        catch (e) { /* private mode / storage disabled - the mode still applies for this page */ }
        if (mode === 'text') {
            (_a = this.session) === null || _a === void 0 ? void 0 : _a.stopRecording();
            (_b = this.shell) === null || _b === void 0 ? void 0 : _b.setInputLevel(0);
            return;
        }
        if (this.isVoiceOrAvatar()) {
            void this.startRecording();
        }
    }
    /** The remembered input mode for this tab, defaulting to voice for a voice-capable avatar. */
    resolveInitialInputMode() {
        if (!this.isVoiceOrAvatar())
            return 'text';
        try {
            return window.sessionStorage.getItem(INPUT_MODE_STORAGE_KEY) === 'text' ? 'text' : 'voice';
        }
        catch (e) {
            return 'voice';
        }
    }
    /** Send a user-typed message from the chat input (shown in chat as a user message). */
    sendText(text) {
        if (!this.session)
            return;
        this.upsertMessage({ id: `user-${Date.now()}`, sender: 'user', content: text, timestamp: Date.now() });
        this.session.sendMessage({
            type: 'conversation.item.create',
            item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] }
        });
        this.session.sendMessage({ type: 'response.create' });
    }
    /**
     * Apply a mood to the avatar. Mood is a client-side rendering concern only
     * (the backend pushes `mood.detected`; the client never sends mood up).
     */
    applyMood(mood) {
        var _a, _b;
        (_a = this.renderer) === null || _a === void 0 ? void 0 : _a.setMood(mood);
        this.fireCallback('avatarMoodChange', mood, (_b = this.shell) === null || _b === void 0 ? void 0 : _b.getStage());
    }
    /**
     * Invoke a user-configured avatar lifecycle callback (from the regular
     * plugin `callbacks` configuration); a throwing callback never breaks the
     * avatar. These fire for every renderer type.
     */
    fireCallback(name, ...args) {
        const callbacks = this.context && this.context.pluginConfiguration
            ? this.context.pluginConfiguration.callbacks
            : undefined;
        const callback = callbacks ? callbacks[name] : undefined;
        if (typeof callback !== 'function')
            return;
        try {
            callback(...args);
        }
        catch (e) {
            console.warn(`[avatar] callbacks.${name} failed`, e);
        }
    }
    // ── Runtime-limit timer (enforcement only) ────────────────────────
    startTimerIfNeeded() {
        var _a;
        const a = this.avatar;
        if (!a)
            return;
        const enabled = a.runtimeLimit === true || a.runtimeLimit === 'true' || a.runtimeLimit === 1 || a.runtimeLimit === '1';
        const seconds = Number((_a = a.runtimeSeconds) !== null && _a !== void 0 ? _a : 0);
        if (!enabled || seconds <= 0)
            return;
        this.remainingSeconds = seconds;
        this.timerHandle = window.setInterval(() => {
            this.remainingSeconds -= 1;
            if (this.remainingSeconds <= 0) {
                this.stopTimer();
                this.end();
            }
        }, 1000);
    }
    stopTimer() {
        if (this.timerHandle !== null) {
            clearInterval(this.timerHandle);
            this.timerHandle = null;
        }
    }
    // ── Teardown ──────────────────────────────────────────────────────
    end() {
        var _a;
        this.stopTimer();
        this.removeAudioUnblock();
        this.connected = false;
        if (this.session) {
            this.session.disconnect();
        }
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.setPhase('ended');
    }
    isVoiceOrAvatar() {
        var _a;
        const t = (_a = this.avatar) === null || _a === void 0 ? void 0 : _a.communicationType;
        return t === 'voice' || t === 'avatar';
    }
    upsertMessage(msg) {
        var _a;
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.upsertMessage(msg);
    }
    /** Full teardown (programmatic — e.g. on page teardown). */
    destroy() {
        document.documentElement.classList.remove(OVERLAY_SCOPE_CLASS);
        this.stopTimer();
        this.removeAudioUnblock();
        if (this.pageHideHandler) {
            window.removeEventListener('pagehide', this.pageHideHandler);
            this.pageHideHandler = null;
        }
        this.connected = false;
        if (this.session) {
            this.session.disconnect();
            this.session = null;
        }
        const hadRenderer = this.renderer !== null;
        if (this.renderer) {
            this.renderer.destroy();
            this.renderer = null;
        }
        if (this.shell) {
            this.shell.destroy();
            this.shell = null;
        }
        if (hadRenderer) {
            this.fireCallback('avatarDestroyed');
        }
    }
}
